import logging from '@wr/logging';
import PropTypes from 'prop-types';
import React, {useCallback} from 'react';
import {connect} from 'react-redux';
import {genericErrorHasOccurred} from '../../../actions/generic-error/action-creators';
import eventIds from '../../../event-ids';
import propLoaders from '../../../prop-loaders';
import {getAvailablePaymentMethods} from '../../../utils/getAvailablePaymentMethods';
import CommonPropTypes from '../../../utils/proptypes';
import {
    getCalculation,
    getCountryCode, getCsrf, getGenericError,
    getLanguageCode, getPlatformData,
    getTranslations
} from '../../../utils/selectors';
import Spinner from '../../atoms/Spinner';
import {PaymentFormTemplate} from '../../organisms';
import ZepzCardSelection from '../../organisms/ZepzCardSelection';
import ZepzForm from '../../organisms/ZepzForm';
import {useZepzPayin} from './zepz-payin.hooks';
import ZepzPayinPropTypes from './zepz-payin.proptypes';
import config from '../../../config';

export const ZepzPayin = ({
    calculation,
    location,
    translations,
    countryCode,
    languageCode,
    match,
    dispatch,
    initFormData,
    initCards,
    genericError,
    csrfToken,
    platform
}) => {
    const {paymentSessionId} = match.params;
    const getPaymentMethods = useCallback(getAvailablePaymentMethods, [countryCode]);

    const {
        cards,
        removeCard,
        showForm,
        toggleFormVisibility
    } = useZepzPayin({
        dispatch,
        csrfToken,
        initCards
    });

    const commonProps = {
        csrfToken,
        translations,
        genericError,
        dispatch,
        toggleFormVisibility,
        platform,
        languageCode,
        paymentSessionId
    };


    if (genericError) {
        return <Spinner />;
    }

    if (!showForm) {
        return (
            <ZepzCardSelection
                cards={cards}
                removeCard={removeCard}
                {...commonProps}
            />
        );
    }


    return (
        <PaymentFormTemplate
            translations={translations}
            cancellationUrl={initFormData?.cancelUrl}
            paymentMethods={getPaymentMethods({countryCode})}
            title={translations.formHeader.payMethod}
            payWithSavedCard={translations.formHeader.payWithSavedCard}
            setCybersourceVisibility={toggleFormVisibility}
            hasSavedCreditCards={cards?.length > 0}
            location={location}
        >
            <ZepzForm
                initData={initFormData}
                calculation={calculation}
                {...commonProps}
            />
        </PaymentFormTemplate>
    );
};


ZepzPayin.getServerSideProps = async ({
    requestId,
    sessionId,
    params: {
        paymentSessionId
    }
}, {dispatch}) => {
    try {
        const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);
        const props = {requestId, sessionId, query: {paymentSessionId}};

        const initFormData = await propLoaders.getZepzPayinInitData(props);

        if (initFormData?.redirectUrl) {
            logger.info('Zepz Payin - user redirected', {
                eventId: eventIds['Zepz Payin - user redirected'],
                requestId,
                sessionId,
                paymentSessionId
            });

            return {
                redirect: initFormData.redirectUrl
            };
        }

        const initCards = await propLoaders.getZepzPayinCards(props);
        return {
            initFormData,
            initCards
        };
    } catch (error) {
        dispatch(genericErrorHasOccurred(true));
        return {
            initFormData: null,
            initCards: null
        };
    }
};

ZepzPayin.propTypes = {
    calculation: ZepzPayinPropTypes.Calculations,
    location: CommonPropTypes.Location.isRequired,
    translations: ZepzPayinPropTypes.Translations.isRequired,
    countryCode: PropTypes.string.isRequired,
    languageCode: PropTypes.string.isRequired,
    match: CommonPropTypes.Match.isRequired,
    dispatch: PropTypes.func.isRequired,
    initFormData: ZepzPayinPropTypes.FormData,
    initCards: PropTypes.arrayOf(ZepzPayinPropTypes.Card),
    genericError: PropTypes.bool,
    csrfToken: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired
};

ZepzPayin.defaultProps = {
    initFormData: undefined,
    initCards: null,
    genericError: null,
    calculation: null
};

const mapStateToProps = state => ({
    csrfToken: getCsrf(state),
    languageCode: getLanguageCode(state),
    translations: getTranslations(state),
    countryCode: getCountryCode(state),
    calculation: getCalculation(state),
    platform: getPlatformData(state)?.platform,
    genericError: getGenericError(state)
});

export default connect(mapStateToProps)(ZepzPayin);
